// Smart Daara - Language Context with i18next
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import i18n from '../i18n';

// Legacy translations for backward compatibility
const legacyTranslations = {
  fr: {
    dashboard: 'Tableau de bord',
    courses: 'Cours',
    students: 'Étudiants',
    teachers: 'Enseignants',
    parents: 'Parents',
    settings: 'Paramètres',
    profile: 'Profil',
    logout: 'Déconnexion',
    login: 'Connexion',
    email: 'Email',
    password: 'Mot de passe',
    welcome: 'Bienvenue',
    notifications: 'Notifications',
    myCourses: 'Mes Cours',
    catalog: 'Catalogue',
    progress: 'Progression',
    lessons: 'Leçons',
    search: 'Rechercher',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    create: 'Créer',
    loading: 'Chargement...',
    completed: 'Terminé',
    inProgress: 'En cours',
    nextLesson: 'Leçon suivante',
    previousLesson: 'Leçon précédente',
    markComplete: 'Marquer terminé',
    readDocument: 'Lire le document',
    classes: 'Classes',
    subjects: 'Matières',
    enrollments: 'Inscriptions',
    subscription: 'Abonnement',
    // Added missing keys
    courseCatalog: 'Catalogue de Cours',
    startCourse: 'Commencer',
    loginTitle: 'Connexion à Smart Daara',
    forgotPassword: 'Mot de passe oublié?',
    firstName: 'Prénom',
    lastName: 'Nom',
    phone: 'Téléphone',
    noAccount: 'Pas de compte?',
    register: "S'inscrire",
    users: 'Utilisateurs',
    features: 'Fonctionnalités',
    pricing: 'Tarifs',
    contact: 'Contact',
    certificates: 'Certificats',
    // Stats & metrics
    totalStudents: 'Étudiants',
    totalTeachers: 'Enseignants',
    totalCourses: 'Cours',
    totalLessons: 'Leçons',
    totalClasses: 'Classes',
    totalParents: 'Parents',
    totalEnrollments: 'Inscriptions',
    activeSubscriptions: 'Abonnements actifs',
    // Dashboard specific
    availableLessons: 'Leçons disponibles',
    learningTime: 'Temps d\'apprentissage',
    completionRate: 'Taux de complétion',
    recentCourses: 'Cours récents',
    latestCoursesAdded: 'Derniers cours ajoutés',
    quickActions: 'Actions rapides',
    activitySummary: "Résumé d'activité",
    weekStatistics: 'Statistiques de la semaine',
    viewAll: 'Voir tout',
    published: 'Publié',
    draft: 'Brouillon',
    schoolOverview: 'Voici un aperçu de votre école',
    addStudent: 'Ajouter un étudiant',
    enrollStudent: 'Inscrire un étudiant',
    addTeacher: 'Ajouter un enseignant',
    addParent: 'Ajouter un parent',
    addCourse: 'Ajouter un cours',
    createCourse: 'Créer un cours',
    manageClasses: 'Gérer les classes',
    viewSubscription: "Voir l'abonnement",
    noCoursesYet: 'Aucun cours pour le moment',
  },
  en: {
    dashboard: 'Dashboard',
    courses: 'Courses',
    students: 'Students',
    teachers: 'Teachers',
    parents: 'Parents',
    settings: 'Settings',
    profile: 'Profile',
    logout: 'Logout',
    login: 'Login',
    email: 'Email',
    password: 'Password',
    welcome: 'Welcome',
    notifications: 'Notifications',
    myCourses: 'My Courses',
    catalog: 'Catalog',
    progress: 'Progress',
    lessons: 'Lessons',
    search: 'Search',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    loading: 'Loading...',
    completed: 'Completed',
    inProgress: 'In Progress',
    nextLesson: 'Next Lesson',
    previousLesson: 'Previous Lesson',
    markComplete: 'Mark Complete',
    readDocument: 'Read Document',
    classes: 'Classes',
    subjects: 'Subjects',
    enrollments: 'Enrollments',
    subscription: 'Subscription',
    // Added missing keys
    courseCatalog: 'Course Catalog',
    startCourse: 'Start Course',
    loginTitle: 'Login to Smart Daara',
    forgotPassword: 'Forgot password?',
    firstName: 'First Name',
    lastName: 'Last Name',
    phone: 'Phone',
    noAccount: 'No account?',
    register: 'Register',
    users: 'Users',
    features: 'Features',
    pricing: 'Pricing',
    contact: 'Contact',
    certificates: 'Certificates',
    // Stats & metrics
    totalStudents: 'Students',
    totalTeachers: 'Teachers',
    totalCourses: 'Courses',
    totalLessons: 'Lessons',
    totalClasses: 'Classes',
    totalParents: 'Parents',
    totalEnrollments: 'Enrollments',
    activeSubscriptions: 'Active subscriptions',
    // Dashboard specific
    availableLessons: 'Available lessons',
    learningTime: 'Learning time',
    completionRate: 'Completion rate',
    recentCourses: 'Recent courses',
    latestCoursesAdded: 'Latest courses added',
    quickActions: 'Quick actions',
    activitySummary: 'Activity summary',
    weekStatistics: "This week's statistics",
    viewAll: 'View all',
    published: 'Published',
    draft: 'Draft',
    schoolOverview: "Here's an overview of your school",
    addStudent: 'Add student',
    enrollStudent: 'Enroll student',
    addTeacher: 'Add teacher',
    addParent: 'Add parent',
    addCourse: 'Add course',
    createCourse: 'Create course',
    manageClasses: 'Manage classes',
    viewSubscription: 'View subscription',
    noCoursesYet: 'No courses yet',
  }
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(i18n.language || 'fr');

  // Sync with i18n on mount
  useEffect(() => {
    const savedLang = localStorage.getItem('smart-daara-language');
    if (savedLang && (savedLang === 'fr' || savedLang === 'en')) {
      setLanguageState(savedLang);
      i18n.changeLanguage(savedLang);
    }
  }, []);

  const setLanguage = useCallback((lang) => {
    if (lang === 'fr' || lang === 'en') {
      setLanguageState(lang);
      localStorage.setItem('smart-daara-language', lang);
      i18n.changeLanguage(lang);
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    const newLang = language === 'fr' ? 'en' : 'fr';
    setLanguage(newLang);
  }, [language, setLanguage]);

  // Translation function - uses legacy translations for backward compatibility
  const t = useCallback((key) => {
    // Use legacy translations directly (flat keys)
    return legacyTranslations[language]?.[key] || legacyTranslations.fr[key] || key;
  }, [language]);

  // Get text based on current language (for objects with _fr suffix)
  const getText = useCallback((obj, field) => {
    if (!obj) return '';
    const frField = `${field}_fr`;
    if (language === 'fr' && obj[frField]) {
      return obj[frField];
    }
    return obj[field] || obj[frField] || '';
  }, [language]);

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    getText,
    i18n, // Expose i18n instance for advanced usage
    isFrench: language === 'fr',
    isEnglish: language === 'en',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
